import { Badge, EmptyState, Skeleton } from '../../shared/ui'
import type { BookingRow } from './useBookings'

interface Props {
  rows: BookingRow[]
}

// Maps the email-send status enum to a canonical <Badge>. Tones follow
// the canonical UX governance:
//   ok    → success states (sent)
//   warn  → soft warnings (host hasn't configured something)
//   err   → hard failures
//   muted → no data
function emailBadge(s: BookingRow['last_email_status']) {
  if (s === 'sent') return <Badge tone="ok">Sent</Badge>
  if (s === 'sent-no-attachments') return <Badge tone="warn">No invite</Badge>
  if (s === 'failed') return <Badge tone="err">Failed</Badge>
  if (s === 'skipped') return <Badge tone="warn">Skipped</Badge>
  return <Badge tone="muted">—</Badge>
}

function eventBadge(s: BookingRow['provider_event_status']) {
  if (s === 'created' || s === 'reinserted') return <Badge tone="ok">Created</Badge>
  if (s === 'updated') return <Badge tone="ok">Moved</Badge>
  if (s === 'deleted' || s === 'gone') return <Badge tone="muted">Removed</Badge>
  if (s === 'failed') return <Badge tone="err">Failed</Badge>
  if (s === 'skipped') return <Badge tone="muted">No calendar</Badge>
  return <Badge tone="muted">—</Badge>
}

export function BookingsTable({ rows }: Props) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Booker</th>
            <th>Event</th>
            <th>Email</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const bookerName = (b.booker_name ?? '').trim()
            const bookerEmail = (b.booker_email ?? '').trim()
            const bookerLabel = bookerName || bookerEmail || '—'
            const titleAttr =
              bookerEmail && bookerName
                ? `${bookerName} <${bookerEmail}>`
                : bookerEmail || bookerName || ''
            const eventErrTitle = b.provider_event_error || b.google_event_error || ''
            const emailErrTitle = b.last_email_error || ''
            return (
              <tr key={b.id ?? b.datetime_iso}>
                <td className="td-mono">{b.date}</td>
                <td className="td-mono">{b.time}</td>
                <td title={titleAttr}>{bookerLabel}</td>
                <td title={eventErrTitle}>
                  {eventBadge(b.provider_event_status ?? b.google_event_status ?? null)}
                </td>
                <td title={emailErrTitle}>{emailBadge(b.last_email_status ?? null)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Skeleton-row loading state. Routes each placeholder through the
// shared <Skeleton> primitive so width modifiers stay typed; the
// underlying `.skel` shimmer is unchanged.
export function BookingsTableSkeleton() {
  const rows = [0, 1, 2, 3]
  return (
    <div className="table-wrap" aria-busy="true" aria-live="polite">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Booker</th>
            <th>Event</th>
            <th>Email</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr key={i} className="skel-row">
              <td>
                <Skeleton width="40" />
              </td>
              <td>
                <Skeleton width="40" />
              </td>
              <td>
                <Skeleton width="70" />
              </td>
              <td>
                <Skeleton width="40" />
              </td>
              <td>
                <Skeleton width="40" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Error state with retry. Routes through the shared <EmptyState>
// primitive (role="alert"); the legacy `.empty-state` card chrome is
// preserved exactly. The previous inline `color: var(--text-dim)` on
// the message was redundant — the legacy `.es-msg` class already
// applies it.
export function BookingsErrorState({
  error,
  onRetry,
}: {
  error: string | null
  onRetry: () => void
}) {
  return (
    <div className="table-wrap">
      <EmptyState
        role="alert"
        emoji="⚠"
        title="Couldn't load bookings"
        message={error || 'Something went wrong fetching your bookings.'}
        cta={{ label: 'Try again', onClick: onRetry }}
      />
    </div>
  )
}

// Empty state. Two branches:
//   - No slug:  onboarding pose, routed through <EmptyState>.
//   - Has slug: "share your link" pose, kept bespoke — see MIGRATION_DEBT.
export function BookingsEmptyState({
  bookingUrl,
  onCopy,
  onOpenSettings,
}: {
  bookingUrl: string | null
  onCopy: () => void
  onOpenSettings: () => void
}) {
  if (!bookingUrl) {
    return (
      <div className="table-wrap">
        <EmptyState
          emoji="◇"
          title="Turn on your booking page"
          message="Pick a handle, your hours and a calendar in a two-minute setup. Then share the link and your first booking lands here."
          cta={{ label: 'Turn on your booking page', onClick: onOpenSettings }}
        />
      </div>
    )
  }

  /*
    MIGRATION_DEBT: "share your link" empty state stays bespoke.
    Reason: two equal-weight CTAs (Copy link + Open) plus a URL display
    block between the message and the action area. The canonical
    <EmptyState> contract is exactly one CTA and title/message/optional-
    emoji only — accepting this surface would require either widening
    the primitive's API (children slot / secondaryCta) or dropping the
    Open link (a UX regression on this surface).

    owner: @shaun
    replace with: <EmptyState> after either
      (a) the Open affordance moves into the URL display (e.g. URL becomes
          a clickable anchor; Copy link remains the only CTA), or
      (b) a second consumer justifies expanding EmptyState's API.
    remove when: condition (a) or (b) is met; on resolution, also fold
      the URL-display block into the primitive (either as message
      content or as a children slot if added).
  */
  return (
    <div className="table-wrap">
      <div className="empty-state">
        <div className="es-emoji" aria-hidden="true">
          ◇
        </div>
        <div className="es-title">No bookings yet</div>
        <div className="es-msg">
          Share your booking link — your first booking will land right here, with confirmation email
          and calendar event status visible at a glance.
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--text)',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 14,
          }}
          title={bookingUrl}
        >
          {bookingUrl.replace(/^https?:\/\//, '')}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="es-cta" onClick={onCopy}>
            Copy link
          </button>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="es-cta"
            style={{
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border2)',
              textDecoration: 'none',
            }}
          >
            Open
          </a>
        </div>
      </div>
    </div>
  )
}
